import { Fragment, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  CUSTOMS_STATUS_LABELS_RU,
  type CustomsSendReportItem,
  type CustomsSendReportRequest,
} from '@crm/shared'
import { Badge } from '../../shared/ui/Badge'

/**
 * Заявки в отчёте «Нетарифка»: менеджера (сверху — с неотправленными) или
 * найденные по партномеру (с колонкой «Менеджер», позиции раскрыты, совпавшие
 * подсвечены). Раскрытие строки — до позиций.
 */

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('ru-RU')
const fmtDateTime = (iso: string) => {
  const d = new Date(iso)
  return `${d.toLocaleDateString('ru-RU')}, ${d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`
}

const LINK_BTN = 'ml-2 text-xs text-brand-600 hover:underline disabled:opacity-50'

/** «Нужна была отправка»: отметка с автором и датой, кнопки поставить и снять. */
function MissedCell({
  item,
  onMark,
  busy,
}: {
  item: CustomsSendReportItem
  onMark?: (itemId: string, missed: boolean) => void
  busy: boolean
}) {
  if (item.missed) {
    return (
      <span className="ml-2 inline-flex items-center">
        <Badge color="red">Нужна была отправка</Badge>
        <span className="ml-1 text-gray-500">
          {fmtDate(item.missed.at)} · {item.missed.byName ?? '—'}
        </span>
        {onMark && (
          <button
            type="button"
            className={LINK_BTN}
            disabled={busy}
            onClick={() => onMark(item.itemId, false)}
          >
            Снять
          </button>
        )}
      </span>
    )
  }
  if (item.sent || !onMark) return null
  return (
    <button
      type="button"
      className="ml-2 rounded border border-red-300 px-2 py-0.5 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50"
      disabled={busy}
      onClick={() => onMark(item.itemId, true)}
      title="Позицию не отправили логисту, а на таможне по ней всплыла пошлина или документы"
    >
      Нужна была отправка
    </button>
  )
}

export function CustomsSendsRequests({
  title,
  requests,
  emptyText,
  onlyUnsent,
  onOnlyUnsentChange,
  showManager = false,
  expandAll = false,
  onMarkMissed,
  markingItemId = null,
}: {
  title: string
  requests: CustomsSendReportRequest[]
  emptyText: string
  onlyUnsent?: boolean
  onOnlyUnsentChange?: (v: boolean) => void
  showManager?: boolean
  expandAll?: boolean
  /** Есть — РОП или админ: кнопка «Нужна была отправка» у неотправленных позиций. */
  onMarkMissed?: (itemId: string, missed: boolean) => void
  markingItemId?: string | null
}) {
  const [openId, setOpenId] = useState<string | null>(null)
  const cols = showManager ? 8 : 7

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {onOnlyUnsentChange && (
          <label className="inline-flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={!!onlyUnsent}
              onChange={(e) => onOnlyUnsentChange(e.target.checked)}
            />
            Только с неотправленными
          </label>
        )}
      </div>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
              <th className="py-2 pr-3 font-medium">Заявка</th>
              {showManager && <th className="px-3 py-2 font-medium">Менеджер</th>}
              <th className="px-3 py-2 font-medium">Клиент</th>
              <th className="px-3 py-2 font-medium">Создана</th>
              <th className="px-3 py-2 text-right font-medium">Из-за рубежа</th>
              <th className="px-3 py-2 font-medium">Отправлено</th>
              <th className="px-3 py-2 font-medium">Когда отправляли</th>
              <th className="py-2 pl-3 font-medium">Внимание от логиста</th>
            </tr>
          </thead>
          <tbody>
            {requests.map((r) => {
              const open = expandAll ? openId !== r.requestId : r.requestId === openId
              const full = r.sent === r.total
              return (
                <Fragment key={r.requestId}>
                  <tr
                    onClick={() =>
                      setOpenId(expandAll ? (open ? r.requestId : null) : open ? null : r.requestId)
                    }
                    className={`cursor-pointer border-b border-gray-50 hover:bg-gray-50 ${open ? 'bg-brand-50' : ''}`}
                    data-testid="customs-report-request"
                  >
                    <td className="py-2 pr-3 font-medium text-gray-800">
                      {open ? '▾' : '▸'}{' '}
                      <Link
                        to={`/requests/${r.requestId}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-brand-600 hover:underline"
                      >
                        {r.requestNumber ?? 'Без номера'}
                      </Link>
                    </td>
                    {showManager && (
                      <td className="px-3 py-2 text-gray-800">{r.managerName ?? '—'}</td>
                    )}
                    <td className="px-3 py-2 text-gray-600">{r.clientName ?? '—'}</td>
                    <td className="px-3 py-2 text-gray-600">{fmtDate(r.createdAt)}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{r.total}</td>
                    <td className="px-3 py-2">
                      <Badge color={full ? 'green' : r.sent === 0 ? 'red' : 'yellow'}>
                        {r.sent} из {r.total}
                      </Badge>
                      {r.missedCount > 0 && (
                        <span className="ml-1">
                          <Badge color="red">нужна была: {r.missedCount}</Badge>
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      {r.lastSentAt ? fmtDateTime(r.lastSentAt) : '—'}
                    </td>
                    <td className="py-2 pl-3">
                      {r.attentionCount > 0 ? (
                        <Badge color="red">{r.attentionCount} с флагом</Badge>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                  {open &&
                    r.items.map((it) => (
                      <tr
                        key={it.itemId}
                        className={`border-b border-gray-50 text-xs ${it.match ? 'bg-yellow-50 font-semibold' : 'bg-gray-50'}`}
                      >
                        <td
                          className="py-1.5 pl-6 pr-3 font-mono text-gray-700"
                          colSpan={showManager ? 3 : 2}
                        >
                          {it.partNumber} · <span className="font-sans">{it.matrix ?? '—'}</span>
                        </td>
                        <td className="px-3 py-1.5" colSpan={2}>
                          {it.sent ? (
                            <Badge color="green">Отправлено</Badge>
                          ) : (
                            <Badge color="red">Не отправлено</Badge>
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-gray-600" colSpan={3}>
                          {it.sentAt ? `${fmtDateTime(it.sentAt)} · ${it.sentByName ?? '—'}` : '—'}
                          {it.status && (
                            <span className="ml-2 text-gray-500">
                              {CUSTOMS_STATUS_LABELS_RU[it.status] ?? it.status}
                            </span>
                          )}
                          {it.attention && (
                            <span className="ml-2">
                              <Badge color="red">Внимание</Badge>
                            </span>
                          )}
                          <MissedCell
                            item={it}
                            onMark={onMarkMissed}
                            busy={markingItemId === it.itemId}
                          />
                        </td>
                      </tr>
                    ))}
                </Fragment>
              )
            })}
            {requests.length === 0 && (
              <tr>
                <td colSpan={cols} className="py-6 text-center text-sm text-gray-400">
                  {emptyText}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
